import { useDarkMode } from '@/contexts/DarkModeContext';
import React from 'react';
import { styled } from 'styled-components';

import { DragHandle } from './DragHandle';

export interface NodeData extends Record<string, unknown> {
  label: string;
  blockType: string;
  config: Record<string, unknown>;
  position?: { x: number; y: number };
  onContextMenu?: (event: React.MouseEvent, nodeId: string) => void;
  // Connection state for visual indicators
  activeConnection?: {
    sourceNodeId: string;
    sourceHandle: string;
  } | null;
  isValidTargetHandle?: (targetHandle: string) => boolean;
  // Connected target handles for dynamic port management
  connectedTargetHandles?: string[];
  // Mode information for readonly behavior
  mode?: 'yaml' | 'gui';
}

export type NodeCategory =
  | 'io'
  | 'flow'
  | 'processing'
  | 'transform'
  | 'document';

export interface BaseNodeProps {
  nodeId: string;
  data: NodeData;
  children?: React.ReactNode;
  category: NodeCategory;
  subtitle?: string;
  selected?: boolean;
  onContextMenu?: (event: React.MouseEvent, nodeId: string) => void;
  // Custom dimensions for dynamic sizing
  width?: number;
  height?: number;
}

// Color themes for each category with dark mode support
const getCategoryThemes = (isDarkMode: boolean) => ({
  io: {
    background: isDarkMode ? '#1e293b' : '#f0f4f8',
    border: isDarkMode ? '#3b82f6' : '#3182ce',
    color: isDarkMode ? '#cbd5e1' : '#2d3748',
    labelColor: isDarkMode ? '#e2e8f0' : '#1a202c',
  },
  flow: {
    background: isDarkMode ? '#1f2937' : '#f7fafc',
    border: isDarkMode ? '#6b7280' : '#4a5568',
    color: isDarkMode ? '#d1d5db' : '#2d3748',
    labelColor: isDarkMode ? '#f3f4f6' : '#1a202c',
  },
  processing: {
    background: isDarkMode ? '#2e1065' : '#faf5ff',
    border: isDarkMode ? '#a855f7' : '#805ad5',
    color: isDarkMode ? '#c4b5fd' : '#553c9a',
    labelColor: isDarkMode ? '#ddd6fe' : '#44337a',
  },
  transform: {
    background: isDarkMode ? '#064e3b' : '#f0fff4',
    border: isDarkMode ? '#10b981' : '#38a169',
    color: isDarkMode ? '#6ee7b7' : '#2f855a',
    labelColor: isDarkMode ? '#a7f3d0' : '#276749',
  },
  document: {
    background: isDarkMode ? '#451a03' : '#fffaf0',
    border: isDarkMode ? '#f59e0b' : '#d69e2e',
    color: isDarkMode ? '#fbbf24' : '#b7791f',
    labelColor: isDarkMode ? '#fcd34d' : '#975a16',
  },
});

const NodeContainer = styled.div<{
  $category: NodeCategory;
  $selected?: boolean;
  $width?: number;
  $height?: number;
  $isDarkMode: boolean;
}>`
  position: relative;
  padding: 8px;
  border-radius: 6px;
  width: ${(props) => (props.$width ? `${props.$width}px` : '160px')};
  height: ${(props) => (props.$height ? `${props.$height}px` : '60px')};
  text-align: center;
  border: 2px solid
    ${(props) => {
      const themes = getCategoryThemes(props.$isDarkMode);
      return props.$selected
        ? props.$isDarkMode
          ? '#3b82f6'
          : '#3182ce'
        : themes[props.$category].border;
    }};
  background-color: ${(props) => {
    const themes = getCategoryThemes(props.$isDarkMode);
    return themes[props.$category].background;
  }};
  color: ${(props) => {
    const themes = getCategoryThemes(props.$isDarkMode);
    return themes[props.$category].color;
  }};
  box-shadow: ${(props) =>
    props.$selected
      ? props.$isDarkMode
        ? `0 0 0 2px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(0, 0, 0, 0.4)`
        : `0 0 0 2px rgba(49, 130, 206, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)`
      : props.$isDarkMode
        ? '0 2px 6px rgba(0, 0, 0, 0.3)'
        : '0 2px 6px rgba(0, 0, 0, 0.12)'};
  transform: ${(props) => (props.$selected ? 'translateY(-1px)' : 'none')};
  transition: all ${({ theme }) => theme.motionDurationMid} ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer; /* Normal pointer cursor for selection */
`;

const NodeLabel = styled.div<{ $category: NodeCategory; $isDarkMode: boolean }>`
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => {
    const themes = getCategoryThemes(props.$isDarkMode);
    return themes[props.$category].labelColor;
  }};
  margin-bottom: 4px;
  line-height: 1.2;
`;

const NodeSubtitle = styled.div<{
  $category: NodeCategory;
  $isDarkMode: boolean;
}>`
  font-size: 10px;
  font-weight: 500;
  color: ${(props) => {
    const themes = getCategoryThemes(props.$isDarkMode);
    return themes[props.$category].color;
  }};
  margin-bottom: 8px;
  opacity: 0.8;
  line-height: 1.1;
`;

export const BaseNode: React.FC<BaseNodeProps> = ({
  nodeId,
  data,
  children,
  category,
  subtitle,
  selected = false,
  onContextMenu,
  width,
  height,
}) => {
  const [isDarkMode] = useDarkMode();

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Use handler from data first, fallback to prop
    if (data.onContextMenu) {
      data.onContextMenu(event, nodeId);
    } else if (onContextMenu) {
      onContextMenu(event, nodeId);
    }
  };

  return (
    <NodeContainer
      $category={category}
      $selected={selected}
      $width={width}
      $height={height}
      $isDarkMode={isDarkMode}
      onContextMenu={handleContextMenu}
    >
      {data.mode === 'gui' && <DragHandle />}
      <NodeLabel
        $category={category}
        $isDarkMode={isDarkMode}
        className="nodrag"
      >
        {String(data.label)}
      </NodeLabel>
      {subtitle && (
        <NodeSubtitle
          $category={category}
          $isDarkMode={isDarkMode}
          className="nodrag"
        >
          {subtitle}
        </NodeSubtitle>
      )}
      <div className="nodrag">{children}</div>
    </NodeContainer>
  );
};
